import { WordGuessLetterStateEnum } from '../interfaces/word-guess-letter-state.enum';

const STATE_PRIORITY: { [state: string]: number } = {
  [WordGuessLetterStateEnum.Empty]: 0,
  [WordGuessLetterStateEnum.Absent]: 1,
  [WordGuessLetterStateEnum.Present]: 2,
  [WordGuessLetterStateEnum.Correct]: 3,
};

/*
 * Scores a guess against the solution.
 *
 * Two passes, so repeated letters are handled the way players expect: every exact hit
 * consumes one occurrence of that letter first, and only the remaining occurrences can be
 * marked as "present". A guess with two E where the solution holds a single E therefore
 * yields one yellow and one grey E, not two yellows.
 *
 * Both words are expected to be normalized (see word-guess-alphabet) and of equal length.
 */
export function evaluateWordGuess(guess: string, solution: string): WordGuessLetterStateEnum[] {
  const guessLetters = guess.split('');
  const solutionLetters = solution.split('');
  const states = guessLetters.map(() => WordGuessLetterStateEnum.Absent);
  const unmatched: { [letter: string]: number } = {};

  guessLetters.forEach((letter, index) => {
    if (letter === solutionLetters[index]) {
      states[index] = WordGuessLetterStateEnum.Correct;
      return;
    }

    const solutionLetter = solutionLetters[index];
    if (solutionLetter !== undefined) {
      unmatched[solutionLetter] = (unmatched[solutionLetter] || 0) + 1;
    }
  });

  guessLetters.forEach((letter, index) => {
    if (states[index] === WordGuessLetterStateEnum.Correct) {
      return;
    }

    if (unmatched[letter]) {
      unmatched[letter] -= 1;
      states[index] = WordGuessLetterStateEnum.Present;
    }
  });

  return states;
}

/*
 * Folds the states of one guess into the keyboard colouring. A key never degrades: once a
 * letter is known to be correct it stays correct, even if a later guess places it wrongly.
 */
export function mergeWordGuessKeyStates(
  currentStates: { [letter: string]: WordGuessLetterStateEnum },
  guess: string,
  states: WordGuessLetterStateEnum[]
): { [letter: string]: WordGuessLetterStateEnum } {
  const merged = { ...currentStates };

  guess.split('').forEach((letter, index) => {
    const next = states[index];
    const current = merged[letter];

    if (!current || STATE_PRIORITY[next] > STATE_PRIORITY[current]) {
      merged[letter] = next;
    }
  });

  return merged;
}

/*
 * Rebuilds the keyboard colouring from scratch for a list of already submitted guesses.
 * Used when a game is restored from storage.
 */
export function buildWordGuessKeyStates(
  guesses: string[],
  solution: string
): { [letter: string]: WordGuessLetterStateEnum } {
  return guesses.reduce(
    (states, guess) => mergeWordGuessKeyStates(states, guess, evaluateWordGuess(guess, solution)),
    {} as { [letter: string]: WordGuessLetterStateEnum }
  );
}
